import React, { useState } from "react";
import { formatCurrency, formatDateDDMMYYYY } from "@/lib/format";
import { fmtQty } from "@/lib/customCalc";
import { IndianRupee, Users, TrendingUp, Wheat, CalendarDays, ChevronDown } from "lucide-react";

const label = (o) => (o.name_local ? `${o.name} · ${o.name_local}` : o.name);
const rateFmt = (r) => (Math.abs(Math.round(r * 100) - r * 100) > 1e-6 ? `₹${Number(r).toFixed(4)}` : formatCurrency(r));

function ItemRow({ k, it, b, sub }) {
  return (
    <div className={`p-3 grid grid-cols-2 sm:grid-cols-[2fr_1fr_1fr_1fr_1fr] gap-1 text-sm items-center ${sub ? "pl-7 bg-[#FFF4E6]/60" : ""}`}>
      <div className="col-span-2 sm:col-span-1 font-semibold">{it.emoji} {label(it)}{it.is_auto_balanced && <span className="ml-1 text-xs bg-thali-turmeric/40 rounded px-1">⚖️ {b("auto")}</span>}</div>
      <div className="text-xs text-thali-ink/70">{fmtQty(it.qty_per_student, it.unit)} / {b("per_student").toLowerCase()}</div>
      <div className="text-xs text-thali-ink/70">{rateFmt(it.rate_per_student)}</div>
      <div className="text-xs text-thali-ink/70">{fmtQty(it.total_quantity, it.unit)}</div>
      <div className="text-right font-bold font-num">{formatCurrency(it.total_cost)}</div>
    </div>
  );
}

function GroupBlock({ g, rows, b }) {
  const [open, setOpen] = useState(false);
  const total = rows.reduce((s, [, it]) => s + it.total_cost, 0);
  return (
    <div data-testid={`cr-group-${g.key}`}>
      <button onClick={() => setOpen((o) => !o)} className="w-full p-3 flex items-center justify-between font-bold text-sm bg-[#FFF4E6]" data-testid={`cr-group-toggle-${g.key}`}>
        <span>{g.emoji} {g.name} ({rows.length})</span>
        <span className="flex items-center gap-1 font-num">{formatCurrency(total)} <ChevronDown className={`w-4 h-4 md:hidden transition-transform ${open ? "rotate-180" : ""}`} /></span>
      </button>
      <div className={`${open ? "block" : "hidden"} md:block divide-y divide-thali-turmeric/10`}>
        {rows.map(([k, it]) => <ItemRow key={k} k={k} it={it} b={b} sub />)}
      </div>
    </div>
  );
}

function ClassCard({ cls, data, b, groups }) {
  const items = Object.entries(data.items);
  const blocks = [];
  items.forEach(([k, it]) => {
    const last = blocks[blocks.length - 1];
    if (it.group && last && last.g === it.group) last.rows.push([k, it]);
    else blocks.push({ g: it.group || null, rows: [[k, it]] });
  });
  return (
    <div className="rounded-card bg-thali-paper shadow-warm overflow-hidden border border-thali-turmeric/20" data-testid={`cr-class-${cls.key}`}>
      <div className="flex items-center justify-between px-4 py-3 text-white" style={{ background: cls.color }}>
        <span className="font-heading font-bold">{cls.emoji} {cls.name} · {data.student_count}</span>
        <span className="font-num font-bold" data-testid={`cr-subtotal-${cls.key}`}>{formatCurrency(data.subtotal)}</span>
      </div>
      <div className="divide-y divide-thali-turmeric/10">
        {items.length === 0 && <div className="p-3 text-sm text-thali-ink/60">{b("day_off")}</div>}
        {blocks.map((blk, i) => (blk.g
          ? <GroupBlock key={i} g={groups.find((x) => x.key === blk.g) || { key: blk.g, name: blk.g, emoji: "🧁" }} rows={blk.rows} b={b} />
          : <ItemRow key={blk.rows[0][0]} k={blk.rows[0][0]} it={blk.rows[0][1]} b={b} />))}
      </div>
      <div className="px-4 py-2 bg-thali-cream flex justify-between text-sm">
        <span>{b("per_student")}</span><span className="font-num font-bold">{formatCurrency(data.per_student_total)}</span>
      </div>
    </div>
  );
}

export default function CustomResultDisplay({ result, b, testid = "custom-result" }) {
  if (!result) return null;
  const s = result.summary;
  const cards = [
    { icon: CalendarDays, label: b("date"), value: `${formatDateDDMMYYYY(result.date)} · ${result.weekday_name}`, c: "#2563EB" },
    { icon: Users, label: b("students"), value: s.total_students, c: "#DB2777" },
    { icon: TrendingUp, label: b("per_student"), value: formatCurrency(s.avg_per_student), c: "#EA580C" },
    { icon: Wheat, label: b("grain_total"), value: `${s.total_rice_kg} kg`, c: "#D97706" },
  ];
  const agg = Object.entries(result.aggregated_items || {});
  return (
    <div className="space-y-4 animate-fade-in-up" data-testid={testid}>
      <div className="rounded-card bg-thali-green text-white p-5 flex items-center justify-between gap-3 shadow-warm">
        <div>
          <div className="text-sm opacity-80 flex items-center gap-1"><IndianRupee className="w-4 h-4" /> {b("grand_total")}</div>
          <div className="text-4xl font-num font-extrabold" data-testid={`${testid}-grand-total`}>{formatCurrency(s.grand_total)}</div>
        </div>
        <div className="text-right text-sm opacity-90 max-w-[50%]">{result.menu_display}<div className="text-xs opacity-70">{result.period_name}</div></div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {cards.map((c, i) => (
          <div key={i} className="rounded-card bg-thali-paper border-l-4 shadow-warm p-3" style={{ borderColor: c.c }}>
            <div className="text-xs text-thali-ink/60 flex items-center gap-1"><c.icon className="w-3 h-3" /> {c.label}</div>
            <div className="font-num font-bold text-thali-ink">{c.value}</div>
          </div>
        ))}
      </div>
      {(result.classes || []).filter((c) => result.categories[c.key]?.student_count > 0).map((c) => (
        <ClassCard key={c.key} cls={c} data={result.categories[c.key]} b={b} groups={result.groups || []} />
      ))}
      {agg.length > 0 && (
        <div className="rounded-card bg-thali-paper shadow-warm overflow-hidden border border-thali-turmeric/20">
          <div className="px-4 py-3 bg-thali-ink text-white font-heading font-bold">Σ</div>
          {agg.map(([k, it]) => (
            <div key={k} className="px-4 py-2 flex justify-between text-sm border-t border-thali-turmeric/10">
              <span className="font-semibold">{it.emoji} {label(it)}</span>
              <span className="text-thali-ink/70">{fmtQty(it.total_quantity, it.unit)} · <b className="font-num text-thali-ink">{formatCurrency(it.total_cost)}</b></span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
